import { getIntegrationManifest } from "#features/conversations/integrations/index.ts";
import type { ConnectionRecord } from "#types/ConnectionRecord.ts";
import type { ConnectedIntegration, GetChannelsDetailsQueryResult } from "#types/types.ts";

type ChannelsDetails = Pick<GetChannelsDetailsQueryResult, "personalIntegrations" | "customMcpServers">;

export const integrationRowKey = (integration: ConnectedIntegration) => `integration:${integration}`;
export const serverRowKey = (definitionId: string) => `server:${definitionId}`;

/** The custom servers the user has connected and can use. */
export function getConnectedServerIds({ customMcpServers }: Pick<ChannelsDetails, "customMcpServers">): Set<string> {
    return new Set(customMcpServers.filter(({ isConnected, isExpired }) => isConnected && !isExpired).map(({ definitionId }) => definitionId));
}

export interface ConnectionRowState {
    key: string;
    isConnected: boolean;
}

/**
 * The rows the request's cards draw, and whether each is connected. Core checks again on resume, and a partial
 * connection does not pass its strict check, so it is not connected here either. An integration the client has no
 * manifest for draws no row, so it cannot hold the resume back.
 */
export function getConnectionRowStates({ integrations, connectableServers }: Pick<ConnectionRecord, "integrations" | "connectableServers">, details: ChannelsDetails): ConnectionRowState[] {
    const connectedServerIds = getConnectedServerIds(details);
    const integrationRows = [...new Set(integrations)].flatMap(integration => {
        const manifest = getIntegrationManifest(integration);

        return manifest
            ? [{
                key: integrationRowKey(integration),
                isConnected: details.personalIntegrations.some(({ channelType, isConnected, isPartiallyConnected }) => channelType === manifest.channelType && isConnected && !isPartiallyConnected)
            }]
            : [];
    });
    const serverRows = [...new Set(connectableServers.map(({ definitionId }) => definitionId))].map(definitionId => ({
        key: serverRowKey(definitionId),
        isConnected: connectedServerIds.has(definitionId)
    }));

    return [...integrationRows, ...serverRows];
}

/**
 * How the request ends once every row is connected or dismissed: `resolved`, or `dismissed` when the user connected none
 * of them. Null while a row is still open. A request with no row to draw has nothing the user can act on, so it ends as
 * dismissed rather than leaving the run paused.
 */
export function getConnectionOutcome(rows: ConnectionRowState[], dismissedKeys: ReadonlySet<string>): "resolved" | "dismissed" | null {
    if (!rows.every(({ key, isConnected }) => isConnected || dismissedKeys.has(key))) {
        return null;
    }

    return rows.some(({ isConnected }) => isConnected) ? "resolved" : "dismissed";
}
